/**
 * Customer Rebuy Order Action
 * Triggered by: arc:action:customerRebuyOrder
 */

export const runCustomerRebuyOrder = async (data = {}) => {
    try {
        const { 
            orderId,
            orderNumber,
            includeAllItems = true,
            selectedLineItemIds = [],
            updateQuantities = {},
            redirectToCart = true,
            form
        } = data;

        if (!orderId && !orderNumber) {
            throw new Error('Either orderId or orderNumber is required');
        }

        // Convert order number to ID if needed
        let finalOrderId = orderId;
        if (!finalOrderId && orderNumber) {
            finalOrderId = orderNumber.replace('#', '');
        }

        // Dispatch rebuy attempt event
        window.dispatchEvent(new CustomEvent('customer:rebuyAttempt', {
            detail: {
                orderId: finalOrderId,
                orderNumber,
                includeAllItems,
                selectedLineItemIds,
                updateQuantities
            }
        }));

        // Get order data first
        const orderData = await fetchOrderData(finalOrderId);
        
        if (!orderData) {
            throw new Error('Order not found or inaccessible');
        }

        // Process line items for re-ordering
        const itemsToAdd = await processOrderItems(orderData, includeAllItems, selectedLineItemIds, updateQuantities);
        
        if (itemsToAdd.length === 0) {
            throw new Error('No items available for re-ordering');
        }

        // Add items to cart
        const cartResponse = await addItemsToCart(itemsToAdd);
        
        if (!cartResponse.success) {
            throw new Error(cartResponse.error || 'Failed to add items to cart');
        }

        // Dispatch success event
        window.dispatchEvent(new CustomEvent('customer:rebuySuccess', {
            detail: {
                orderId: finalOrderId,
                orderNumber,
                itemsAdded: itemsToAdd,
                cartResponse: cartResponse.data,
                timestamp: Date.now()
            }
        }));

        if (redirectToCart) {
            // Redirect to cart page
            window.location.href = '/cart';
        }

        return {
            success: true,
            orderId: finalOrderId,
            itemsAdded: itemsToAdd,
            cartResponse: cartResponse.data,
            message: 'Items successfully added to cart'
        };

    } catch (error) {
        console.error('Customer Rebuy Order error:', error);
        
        window.dispatchEvent(new CustomEvent('customer:rebuyError', {
            detail: {
                error: error.message,
                orderId: data.orderId || data.orderNumber
            }
        }));

        return {
            success: false,
            error: error.message
        };
    }
};

// Fetch order data from customer account
const fetchOrderData = async (orderId) => {
    try {
        // This would typically require authentication or a special endpoint
        // For now, we'll assume the order data is available in the DOM or via an API
        
        // Try to get order data from the current page if it's an order details page
        const orderElement = document.querySelector(`[data-order-id="${orderId}"]`);
        if (orderElement && orderElement.dataset.orderData) {
            return JSON.parse(orderElement.dataset.orderData);
        }

        // Alternative: fetch from customer account API if available
        const response = await fetch(`/account/orders/${orderId}.json`, {
            headers: {
                'Accept': 'application/json'
            }
        });

        if (response.ok) {
            return await response.json();
        }

        return null;
    } catch (error) {
        console.error('Error fetching order data:', error);
        return null;
    }
};

// Process order items for re-ordering
const processOrderItems = async (orderData, includeAllItems, selectedLineItemIds, updateQuantities) => {
    const items = [];
    const lineItems = orderData.line_items || orderData.lineItems || [];

    for (const lineItem of lineItems) {
        // Skip if not including all items and this item isn't selected
        if (!includeAllItems && !selectedLineItemIds.includes(lineItem.id)) {
            continue;
        }

        // Check if variant is still available
        const variantId = lineItem.variant_id || lineItem.variantId;
        if (!variantId) continue;

        const isAvailable = await checkVariantAvailability(variantId);
        if (!isAvailable) {
            // Dispatch unavailable item event
            window.dispatchEvent(new CustomEvent('customer:rebuyItemUnavailable', {
                detail: {
                    lineItem,
                    variantId
                }
            }));
            continue;
        }

        // Determine quantity
        let quantity = lineItem.quantity;
        if (updateQuantities[lineItem.id]) {
            quantity = updateQuantities[lineItem.id];
        }

        items.push({
            id: variantId,
            quantity: quantity,
            properties: lineItem.properties || {}
        });
    }

    return items;
};

// Check if variant is still available
const checkVariantAvailability = async (variantId) => {
    try {
        // Quick availability check via product page or API
        const response = await fetch(`/products.json?variant_ids=${variantId}`, {
            headers: {
                'Accept': 'application/json'
            }
        });

        if (response.ok) {
            const data = await response.json();
            const product = data.products?.[0];
            const variant = product?.variants?.find(v => v.id == variantId);
            return variant?.available || false;
        }

        return false;
    } catch (error) {
        console.error('Error checking variant availability:', error);
        return false;
    }
};

// Add items to cart
const addItemsToCart = async (items) => {
    try {
        const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            body: JSON.stringify({
                items: items
            })
        });

        if (response.ok) {
            const data = await response.json();
            return {
                success: true,
                data: data
            };
        } else {
            const errorData = await response.json();
            return {
                success: false,
                error: errorData.message || 'Failed to add items to cart'
            };
        }
    } catch (error) {
        return {
            success: false,
            error: error.message
        };
    }
};

// Initialize rebuy order listeners
export const initializeCustomerRebuyOrder = (config = {}) => {
    const {
        rebuyButtonSelector = '[data-rebuy-order]',
        rebuyFormSelector = '[data-rebuy-order-form]',
        autoSetupButtons = true
    } = config;

    if (autoSetupButtons) {
        setupRebuyOrderButtons(rebuyButtonSelector);
        setupRebuyOrderForms(rebuyFormSelector);
    }

    window.dispatchEvent(new CustomEvent('customerRebuyOrder:initialized', {
        detail: { config, timestamp: Date.now() }
    }));
};

// Setup rebuy order button listeners
const setupRebuyOrderButtons = (buttonSelector) => {
    const rebuyButtons = document.querySelectorAll(buttonSelector);
    
    rebuyButtons.forEach(button => {
        button.removeEventListener('click', handleRebuyButtonClick);
        button.addEventListener('click', handleRebuyButtonClick);
    });
};

// Setup rebuy order form listeners
const setupRebuyOrderForms = (formSelector) => {
    const rebuyForms = document.querySelectorAll(formSelector);
    
    rebuyForms.forEach(form => {
        form.removeEventListener('submit', handleRebuyFormSubmit);
        form.addEventListener('submit', handleRebuyFormSubmit);
    });
};

// Handle rebuy button clicks
const handleRebuyButtonClick = async (event) => {
    event.preventDefault();
    
    const button = event.target;
    const orderId = button.dataset.orderId;
    const orderNumber = button.dataset.orderNumber;
    const redirectToCart = button.dataset.redirectToCart !== 'false';

    if (!orderId && !orderNumber) {
        console.error('Order ID or number not found for rebuy button');
        return;
    }

    // Disable button during processing
    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding to cart...';

    try {
        await window.dispatchEvent(new CustomEvent('arc:action:customerRebuyOrder', {
            detail: {
                orderId,
                orderNumber,
                includeAllItems: true,
                redirectToCart
            }
        }));
    } finally {
        // Re-enable button
        if (button.isConnected) {
            button.disabled = false;
            button.textContent = originalText;
        }
    }
};

// Handle rebuy form submissions (for selective item reordering)
const handleRebuyFormSubmit = async (event) => {
    event.preventDefault();
    
    const form = event.target;
    const formData = new FormData(form);
    
    const orderId = form.dataset.orderId;
    const orderNumber = form.dataset.orderNumber;
    const redirectToCart = form.dataset.redirectToCart !== 'false';

    // Get selected line items
    const selectedLineItemIds = [];
    const updateQuantities = {};
    
    for (let [key, value] of formData.entries()) {
        if (key.startsWith('line_item_')) {
            const lineItemId = key.replace('line_item_', '');
            if (value === 'on') {
                selectedLineItemIds.push(lineItemId);
            }
        } else if (key.startsWith('quantity_')) {
            const lineItemId = key.replace('quantity_', '');
            updateQuantities[lineItemId] = parseInt(value) || 1;
        }
    }

    // Disable submit button
    const submitBtn = form.querySelector('[type="submit"]');
    const originalText = submitBtn?.textContent;
    if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Adding to cart...';
    }

    try {
        await window.dispatchEvent(new CustomEvent('arc:action:customerRebuyOrder', {
            detail: {
                orderId,
                orderNumber,
                includeAllItems: selectedLineItemIds.length === 0,
                selectedLineItemIds,
                updateQuantities,
                redirectToCart,
                form
            }
        }));
    } finally {
        // Re-enable submit button
        if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.textContent = originalText;
        }
    }
};

// Helper function to rebuy a single item
export const rebuyOrderItem = async (lineItemData) => {
    try {
        const { variantId, quantity = 1, properties = {} } = lineItemData;

        if (!variantId) {
            throw new Error('Variant ID is required');
        }

        // Check availability
        const isAvailable = await checkVariantAvailability(variantId);
        if (!isAvailable) {
            throw new Error('This item is no longer available');
        }

        // Add to cart
        const cartResponse = await addItemsToCart([{
            id: variantId,
            quantity,
            properties
        }]);

        if (cartResponse.success) {
            window.dispatchEvent(new CustomEvent('customer:rebuyItemSuccess', {
                detail: {
                    variantId,
                    quantity,
                    cartResponse: cartResponse.data
                }
            }));

            return {
                success: true,
                message: 'Item added to cart successfully'
            };
        } else {
            throw new Error(cartResponse.error);
        }

    } catch (error) {
        window.dispatchEvent(new CustomEvent('customer:rebuyItemError', {
            detail: {
                error: error.message,
                variantId: lineItemData.variantId
            }
        }));

        return {
            success: false,
            error: error.message
        };
    }
};

export default runCustomerRebuyOrder;
